import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@/lib/router";
import { useQuery } from "@tanstack/react-query";
import { dashboardApi } from "../api/dashboard";
import { activityApi } from "../api/activity";
import { issuesApi } from "../api/issues";
import { agentsApi } from "../api/agents";
import { projectsApi } from "../api/projects";
import { heartbeatsApi } from "../api/heartbeats";
import { graphsApi, type AgentGraph } from "../api/graphs";
import { usePractice } from "../context/PracticeContext";
import { useDialog } from "../context/DialogContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { queryKeys } from "../lib/queryKeys";
import { MetricCard } from "../components/MetricCard";
import { EmptyState } from "../components/EmptyState";
import { StatusIcon } from "../components/StatusIcon";
import { PriorityIcon } from "../components/PriorityIcon";
import { ActivityRow } from "../components/ActivityRow";
import { Identity } from "../components/Identity";
import { timeAgo } from "../lib/timeAgo";
import { cn, formatCents } from "../lib/utils";
import { AlertTriangle, Bot, Check, CircleDot, DollarSign, ShieldCheck, LayoutDashboard, Workflow } from "lucide-react";
import { ActiveAgentsPanel } from "../components/ActiveAgentsPanel";
import { ChartCard, RunActivityChart, PriorityChart, IssueStatusChart, SuccessRateChart } from "../components/ActivityCharts";
import { PageSkeleton } from "../components/PageSkeleton";
import { metricAlertsApi } from "../api/metricAlerts";
import type { Agent, Issue, MetricAlert } from "@ligament/shared";

function getRecentIssues(issues: Issue[]): Issue[] {
  return [...issues]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

export function Dashboard() {
  const { selectedPracticeId, companies } = usePractice();
  const { openOnboarding } = useDialog();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [animatedActivityIds, setAnimatedActivityIds] = useState<Set<string>>(new Set());
  const seenActivityIdsRef = useRef<Set<string>>(new Set());
  const hydratedActivityRef = useRef(false);
  const activityAnimationTimersRef = useRef<number[]>([]);

  const { data: agents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  useEffect(() => {
    setBreadcrumbs([{ label: "Dashboard" }]);
  }, [setBreadcrumbs]);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.dashboard(selectedPracticeId!),
    queryFn: () => dashboardApi.summary(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: activity } = useQuery({
    queryKey: queryKeys.activity(selectedPracticeId!),
    queryFn: () => activityApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: issues } = useQuery({
    queryKey: queryKeys.issues.list(selectedPracticeId!),
    queryFn: () => issuesApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: projects } = useQuery({
    queryKey: queryKeys.projects.list(selectedPracticeId!),
    queryFn: () => projectsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: runs } = useQuery({
    queryKey: queryKeys.heartbeats(selectedPracticeId!),
    queryFn: () => heartbeatsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: workflows } = useQuery({
    queryKey: ["graphs", selectedPracticeId],
    queryFn: () => graphsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: metricAlerts } = useQuery({
    queryKey: ["metric-alerts", selectedPracticeId],
    queryFn: () => metricAlertsApi.list(selectedPracticeId!, { acknowledged: false, limit: 10 }),
    enabled: !!selectedPracticeId,
  });

  const recentIssues = issues ? getRecentIssues(issues) : [];
  const recentActivity = useMemo(() => (activity ?? []).slice(0, 10), [activity]);

  useEffect(() => {
    for (const timer of activityAnimationTimersRef.current) {
      window.clearTimeout(timer);
    }
    activityAnimationTimersRef.current = [];
    seenActivityIdsRef.current = new Set();
    hydratedActivityRef.current = false;
    setAnimatedActivityIds(new Set());
  }, [selectedPracticeId]);

  useEffect(() => {
    if (recentActivity.length === 0) return;

    const seen = seenActivityIdsRef.current;
    const currentIds = recentActivity.map((event) => event.id);

    if (!hydratedActivityRef.current) {
      for (const id of currentIds) seen.add(id);
      hydratedActivityRef.current = true;
      return;
    }

    const newIds = currentIds.filter((id) => !seen.has(id));
    if (newIds.length === 0) {
      for (const id of currentIds) seen.add(id);
      return;
    }

    setAnimatedActivityIds((prev) => {
      const next = new Set(prev);
      for (const id of newIds) next.add(id);
      return next;
    });

    for (const id of newIds) seen.add(id);

    const timer = window.setTimeout(() => {
      setAnimatedActivityIds((prev) => {
        const next = new Set(prev);
        for (const id of newIds) next.delete(id);
        return next;
      });
      activityAnimationTimersRef.current = activityAnimationTimersRef.current.filter((t) => t !== timer);
    }, 980);
    activityAnimationTimersRef.current.push(timer);
  }, [recentActivity]);

  useEffect(() => {
    return () => {
      for (const timer of activityAnimationTimersRef.current) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  const agentMap = useMemo(() => {
    const map = new Map<string, Agent>();
    for (const a of agents ?? []) map.set(a.id, a);
    return map;
  }, [agents]);

  const entityNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of issues ?? []) map.set(`issue:${i.id}`, i.identifier ?? i.id.slice(0, 8));
    for (const a of agents ?? []) map.set(`agent:${a.id}`, a.name);
    for (const p of projects ?? []) map.set(`project:${p.id}`, p.name);
    return map;
  }, [issues, agents, projects]);

  const entityTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of issues ?? []) map.set(`issue:${i.id}`, i.title);
    return map;
  }, [issues]);

  const agentName = (id: string | null) => {
    if (!id || !agents) return null;
    return agents.find((a) => a.id === id)?.name ?? null;
  };

  if (!selectedPracticeId) {
    if (companies.length === 0) {
      return (
        <EmptyState
          icon={LayoutDashboard}
          message="Welcome to Ligament. Set up your first practice and agent to get started."
          action="Get Started"
          onAction={openOnboarding}
        />
      );
    }
    return (
      <EmptyState icon={LayoutDashboard} message="Create or select a practice to view the dashboard." />
    );
  }

  if (isLoading) {
    return <PageSkeleton variant="dashboard" />;
  }

  const hasNoAgents = agents !== undefined && agents.length === 0;

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {hasNoAgents && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/25 dark:bg-amber-950/60">
          <div className="flex items-center gap-2.5">
            <Bot className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-sm text-amber-900 dark:text-amber-100">
              You have no agents.
            </p>
          </div>
          <button
            onClick={() => openOnboarding({ initialStep: 2, companyId: selectedPracticeId! })}
            className="text-sm font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100 underline underline-offset-2 shrink-0"
          >
            Create one here
          </button>
        </div>
      )}

      <ActiveAgentsPanel companyId={selectedPracticeId!} />

      {/* Metric Alerts */}
      {metricAlerts && metricAlerts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Metric Alerts
          </h3>
          <div className="space-y-2">
            {metricAlerts.map((alert) => {
              const severityStyles: Record<string, string> = {
                critical: "border-red-300 bg-red-50 dark:border-red-500/25 dark:bg-red-950/60",
                warning: "border-amber-300 bg-amber-50 dark:border-amber-500/25 dark:bg-amber-950/60",
                info: "border-blue-300 bg-blue-50 dark:border-blue-500/25 dark:bg-blue-950/60",
              };
              const iconStyles: Record<string, string> = {
                critical: "text-red-600 dark:text-red-400",
                warning: "text-amber-600 dark:text-amber-400",
                info: "text-blue-600 dark:text-blue-400",
              };
              const textStyles: Record<string, string> = {
                critical: "text-red-900 dark:text-red-100",
                warning: "text-amber-900 dark:text-amber-100",
                info: "text-blue-900 dark:text-blue-100",
              };
              return (
                <div
                  key={alert.id}
                  className={cn(
                    "flex items-start gap-3 rounded-md border px-4 py-3",
                    severityStyles[alert.severity] ?? severityStyles.info,
                  )}
                >
                  <AlertTriangle className={cn("h-4 w-4 shrink-0 mt-0.5", iconStyles[alert.severity] ?? iconStyles.info)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn("text-sm font-medium", textStyles[alert.severity] ?? textStyles.info)}>
                        {alert.thresholdLabel}
                      </span>
                      <span className={cn(
                        "text-[10px] font-medium px-1.5 py-0.5 rounded-sm uppercase",
                        alert.severity === "critical"
                          ? "bg-red-200 text-red-800 dark:bg-red-800/30 dark:text-red-300"
                          : alert.severity === "warning"
                            ? "bg-amber-200 text-amber-800 dark:bg-amber-800/30 dark:text-amber-300"
                            : "bg-blue-200 text-blue-800 dark:bg-blue-800/30 dark:text-blue-300",
                      )}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className={cn("text-xs mt-0.5", textStyles[alert.severity] ?? textStyles.info)}>
                      {alert.message}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{timeAgo(alert.createdAt)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-1 sm:gap-2">
            <MetricCard
              icon={Bot}
              value={(data.agents?.active ?? 0) + (data.agents?.running ?? 0) + (data.agents?.paused ?? 0) + (data.agents?.error ?? 0)}
              label="Agents Enabled"
              to="/agents"
              description={
                <span>
                  {data.agents?.running ?? 0} running{", "}
                  {data.agents?.paused ?? 0} paused{", "}
                  {data.agents?.error ?? 0} errors
                </span>
              }
            />
            <MetricCard
              icon={Workflow}
              value={workflows?.length ?? 0}
              label="Workflows"
              to="/studio"
              description={
                <span>
                  {workflows?.filter((w) => w.status === "deployed").length ?? 0} deployed{", "}
                  {workflows?.filter((w) => w.status === "draft").length ?? 0} draft
                </span>
              }
            />
            <MetricCard
              icon={CircleDot}
              value={data.tasks?.inProgress ?? 0}
              label="Tasks In Progress"
              to="/issues"
              description={
                <span>
                  {data.tasks?.open ?? 0} open{", "}
                  {data.tasks?.blocked ?? 0} blocked
                </span>
              }
            />
            <MetricCard
              icon={DollarSign}
              value={formatCents(data.costs?.monthSpendCents ?? 0)}
              label="Month Spend"
              to="/costs"
              description={
                <span>
                  {(data.costs?.monthBudgetCents ?? 0) > 0
                    ? `${data.costs?.monthUtilizationPercent ?? 0}% of ${formatCents(data.costs?.monthBudgetCents ?? 0)} budget`
                    : "Unlimited budget"}
                </span>
              }
            />
            <MetricCard
              icon={ShieldCheck}
              value={data.pendingApprovals ?? 0}
              label="Pending Approvals"
              to="/approvals"
              description={
                <span>
                  {data.staleTasks ?? 0} stale tasks
                </span>
              }
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ChartCard title="Run Activity" subtitle="Last 14 days" stat={(runs ?? []).length} statSuffix="runs">
              <RunActivityChart runs={runs ?? []} />
            </ChartCard>
            <ChartCard title="Issues by Priority" subtitle="Last 14 days" stat={(issues ?? []).length} statSuffix="total">
              <PriorityChart issues={issues ?? []} />
            </ChartCard>
            <ChartCard title="Issues by Status" subtitle="Last 14 days" stat={(issues ?? []).length} statSuffix="issues">
              <IssueStatusChart issues={issues ?? []} />
            </ChartCard>
            <ChartCard
              title="Success Rate"
              subtitle="Last 14 days"
              stat={(() => {
                const r = runs ?? [];
                const succeeded = r.filter((run) => run.status === "succeeded").length;
                return r.length > 0 ? `${Math.round((succeeded / r.length) * 100)}%` : "—";
              })()}
            >
              <SuccessRateChart runs={runs ?? []} />
            </ChartCard>
          </div>

          {/* Workflows — only show graphs with wf_start nodes */}
          {(() => {
            const wfGraphs = (workflows ?? []).filter((wf) =>
              wf.graphDefinition?.nodes?.some((n) => n.type === "wf_start"),
            );
            if (wfGraphs.length === 0) return null;
            return (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Workflows
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {wfGraphs.map((wf) => {
                    const nodeCount = wf.graphDefinition?.nodes?.length ?? 0;
                    const agentNodes = wf.graphDefinition?.nodes?.filter((n) => n.type === "agent_ref").length ?? 0;
                    return (
                      <Link
                        key={wf.id}
                        to={`/workflows/${wf.id}`}
                        className="border border-border p-4 hover:bg-accent/50 transition-colors no-underline text-inherit block"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Workflow className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{wf.name}</span>
                          <span className={cn(
                            "ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-sm shrink-0",
                            wf.status === "deployed"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                              : "bg-muted text-muted-foreground",
                          )}>
                            {wf.status}
                          </span>
                        </div>
                        {wf.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mb-1">{wf.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {nodeCount} steps &middot; {agentNodes} agents
                        </p>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Agents */}
          {agents && agents.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Agents
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {agents.map((agent) => {
                  const statusColors: Record<string, string> = {
                    active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                    running: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                    idle: "bg-muted text-muted-foreground",
                    paused: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                    error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                  };
                  return (
                    <Link
                      key={agent.id}
                      to={`/agents/${agent.id}`}
                      className="border border-border p-4 hover:bg-accent/50 transition-colors no-underline text-inherit block"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Bot className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium truncate">{agent.name}</span>
                        <span className={cn(
                          "ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-sm shrink-0",
                          statusColors[agent.status] ?? "bg-muted text-muted-foreground",
                        )}>
                          {agent.status}
                        </span>
                      </div>
                      {agent.capabilities && (
                        <p className="text-xs text-muted-foreground line-clamp-1 mb-1">{agent.capabilities}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {agent.adapterType ?? "bedrock"} &middot; {timeAgo(agent.updatedAt)}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Recent Activity */}
            {recentActivity.length > 0 && (
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Recent Activity
                </h3>
                <div className="border border-border divide-y divide-border overflow-hidden">
                  {recentActivity.map((event) => (
                    <ActivityRow
                      key={event.id}
                      event={event}
                      agentMap={agentMap}
                      entityNameMap={entityNameMap}
                      entityTitleMap={entityTitleMap}
                      className={animatedActivityIds.has(event.id) ? "activity-row-enter" : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Recent Tasks */}
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Recent Tasks
              </h3>
              {recentIssues.length === 0 ? (
                <div className="border border-border p-4">
                  <p className="text-sm text-muted-foreground">No tasks yet.</p>
                </div>
              ) : (
                <div className="border border-border divide-y divide-border overflow-hidden">
                  {recentIssues.slice(0, 10).map((issue) => (
                    <Link
                      key={issue.id}
                      to={`/issues/${issue.identifier ?? issue.id}`}
                      className="px-4 py-3 text-sm cursor-pointer hover:bg-accent/50 transition-colors no-underline text-inherit block"
                    >
                      <div className="flex items-start gap-2 sm:items-center sm:gap-3">
                        {/* Status icon - left column on mobile */}
                        <span className="shrink-0 sm:hidden">
                          <StatusIcon status={issue.status} />
                        </span>

                        {/* Right column on mobile: title + metadata stacked */}
                        <span className="flex min-w-0 flex-1 flex-col gap-1 sm:contents">
                          <span className="line-clamp-2 text-sm sm:order-2 sm:flex-1 sm:min-w-0 sm:line-clamp-none sm:truncate">
                            {issue.title}
                          </span>
                          <span className="flex items-center gap-2 sm:order-1 sm:shrink-0">
                            <span className="hidden sm:inline-flex"><PriorityIcon priority={issue.priority} /></span>
                            <span className="hidden sm:inline-flex"><StatusIcon status={issue.status} /></span>
                            <span className="text-xs font-mono text-muted-foreground">
                              {issue.identifier ?? issue.id.slice(0, 8)}
                            </span>
                            {issue.assigneeAgentId && (() => {
                              const name = agentName(issue.assigneeAgentId);
                              return name
                                ? <span className="hidden sm:inline-flex"><Identity name={name} size="sm" /></span>
                                : null;
                            })()}
                            <span className="text-xs text-muted-foreground sm:hidden">&middot;</span>
                            <span className="text-xs text-muted-foreground shrink-0 sm:order-last">
                              {timeAgo(issue.updatedAt)}
                            </span>
                          </span>
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
}
